import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { auth, CompleteUser } from '../api/auth';
import { config } from '../config';
import api, { AUTH_LOGOUT_EVENT_NAME, authLogoutEvent } from '../api';

// User interface is imported from auth.ts

interface AuthContextType {
  user: CompleteUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  pageTitle: string;
  setPageTitle: (title: string) => void;
  login: (email: string, password: string) => Promise<CompleteUser | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CompleteUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start as true to prevent flash of content/redirects
  const [pageTitle, setPageTitle] = useState<string>('');

  const buildCompleteUser = useCallback(
    async (baseUser: CompleteUser): Promise<CompleteUser> => {
      try {
        const profileResponse = await api.get('/profile/me');
        const resolvedRole =
          typeof profileResponse.data?.role === 'string'
            ? profileResponse.data.role.toLowerCase()
            : baseUser.role.toLowerCase();
        return {
          ...baseUser,
          ...profileResponse.data,
          role: resolvedRole,
        };
      } catch (profileError) {
        console.error('Error fetching profile:', profileError);
        return {
          ...baseUser,
          role: baseUser.role.toLowerCase(),
        };
      }
    },
    [],
  );

  useEffect(() => {
    const initAuth = async () => {
      try {
        const session = await auth.getSession();
        const completeUserData = await buildCompleteUser(session.user);
        setToken('cookie-session');
        setUser(completeUserData);
        localStorage.removeItem(config.auth.storageKeys.token);
        localStorage.removeItem(config.auth.storageKeys.user);
      } catch (sessionError) {
        const storedToken = localStorage.getItem(config.auth.storageKeys.token);
        const storedUser = localStorage.getItem(config.auth.storageKeys.user);

        if (storedToken && storedUser) {
          try {
            auth.setAuthToken(storedToken);
            const parsedUser = JSON.parse(storedUser);
            const completeUserData = await buildCompleteUser(parsedUser);
            setToken(storedToken);
            setUser(completeUserData);
          } catch (legacyError) {
            console.error('Error restoring legacy auth data:', legacyError);
            auth.setAuthToken(null);
            localStorage.removeItem(config.auth.storageKeys.token);
            localStorage.removeItem(config.auth.storageKeys.user);
          }
        } else {
          auth.setAuthToken(null);
          setToken(null);
          setUser(null);
        }

        if (sessionError instanceof Error) {
          console.debug('No active cookie session found:', sessionError.message);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [buildCompleteUser]);

  const login = useCallback(async (email: string, password: string): Promise<CompleteUser | null> => {
    setIsLoading(true);
    try {
      const { token: newToken, user: newUser } = await auth.login({ email, password });
      auth.setAuthToken(null);
      localStorage.removeItem(config.auth.storageKeys.token);
      localStorage.removeItem(config.auth.storageKeys.user);

      const completeUserData = await buildCompleteUser(newUser);
      setToken(newToken || 'cookie-session');
      setUser(completeUserData);

      return completeUserData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [buildCompleteUser]);

  const logout = useCallback(() => {
    void auth.logout();
    auth.setAuthToken(null);
    setUser(null);
    setToken(null);
    localStorage.removeItem(config.auth.storageKeys.token);
    localStorage.removeItem(config.auth.storageKeys.user);
  }, []);

  useEffect(() => {
    setPageTitle('');
  }, [setPageTitle]);

  /**
   * Listener global para evento de logout disparado pelo Axios Interceptor
   * (quando sessão 401 expira, mesmo depois de refresh).
   * Chamamos logout() que roda a rota /auth/logout no backend, apaga storage
   * e seta user=null → routes.tsx detecta e redireciona pro login SEM
   * reload completo da página (window.location.href).
   */
  useEffect(() => {
    const handleForceLogout = () => {
      void logout();
    };
    const target = authLogoutEvent as EventTarget;
    target.addEventListener(AUTH_LOGOUT_EVENT_NAME, handleForceLogout);
    return () => {
      target.removeEventListener(AUTH_LOGOUT_EVENT_NAME, handleForceLogout);
    };
  }, [logout]);

  const value = {
    user,
    token,
    isAuthenticated: !!user,
    isLoading,
    pageTitle,
    setPageTitle,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 
